import { forwardRef } from 'react'
import clsx from 'clsx'
import LoadingSpinner from './LoadingSpinner'

/**
 * Button Component
 * Reusable button with multiple variants and states
 */
const Button = forwardRef(function Button(
  {
    as: Component = 'button',
    children,
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    fullWidth = false,
    leftIcon,
    rightIcon,
    className = '',
    type = 'button',
    onClick,
    ...props
  },
  ref
) {
  const baseClasses = 'btn'

  const variantClasses = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    outline: 'btn-outline',
    ghost: 'btn-ghost'
  }

  const sizeClasses = {
    sm: 'min-h-10 px-3 py-1.5 text-sm',
    md: 'min-h-11 px-4 py-2 text-sm',
    lg: 'btn-lg',
    xl: 'btn-xl',
    icon: 'h-11 w-11 p-0'
  }

  const buttonClasses = clsx(
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    {
      'w-full': fullWidth,
      'cursor-not-allowed opacity-50': disabled || loading,
      'cursor-wait': loading
    },
    className
  )

  const isDisabled = disabled || loading
  const isNativeButton = Component === 'button'

  const handleClick = event => {
    if (isDisabled) {
      event.preventDefault()
      return
    }

    onClick?.(event)
  }

  return (
    <Component
      ref={ref}
      className={buttonClasses}
      {...props}
      onClick={handleClick}
      aria-busy={loading || undefined}
      {...(isNativeButton
        ? { type, disabled: isDisabled }
        : {
            'aria-disabled': isDisabled || undefined,
            ...(isDisabled ? { tabIndex: -1 } : {})
          })}
    >
      <span
        className={clsx(
          'flex min-w-0 max-w-full items-center justify-center gap-2 text-center [overflow-wrap:anywhere]',
          loading && 'opacity-0'
        )}
      >
        {leftIcon && <span className="shrink-0">{leftIcon}</span>}
        {children}
        {rightIcon && <span className="shrink-0">{rightIcon}</span>}
      </span>
      {loading && (
        <span
          className="absolute inset-0 flex items-center justify-center"
          aria-hidden="true"
        >
          <LoadingSpinner
            size="sm"
            color={variant === 'primary' ? 'white' : 'primary'}
          />
        </span>
      )}
    </Component>
  )
})

Button.displayName = 'Button'

export default Button
